import React, { Fragment, useCallback, useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader } from "../reporting/ReportingShared";
import { getLoyaltyList, getMerchantListing } from "../../utils/apiCalls";

const buttonStyle = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    border: "none", cursor: "pointer", transition: "all 0.15s",
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)", textDecoration: "none",
    display: "inline-flex", alignItems: "center", gap: 6,
};

const Loyalty = () => {
    const navigate = useNavigate();
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const [loyaltyData, setLoyaltyData] = useState([]);
    const [merchantList, setMerchantList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        const merchantFilter = (userType === "merchant")
            ? merchantID
            : (selectedMerchant && selectedMerchant !== "all") ? selectedMerchant : undefined;
        const data = await getLoyaltyList(merchantFilter);
        setLoyaltyData(data || []);
        setIsLoading(false);
    }, [selectedMerchant, userType, merchantID]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (userType !== "merchant") {
            (async () => setMerchantList(await getMerchantListing()))();
        }
    }, [userType]);

    const tiles = useMemo(() => {
        const total = loyaltyData.length;
        const active = loyaltyData.filter(l => l.isActive).length;
        const stamps = loyaltyData.filter(l => l.loyaltyType === "stamps").length;
        const percentage = loyaltyData.filter(l => l.loyaltyType === "percentage").length;
        return { total, active, stamps, percentage };
    }, [loyaltyData]);

    const merchantNameById = (id) => merchantList.find(m => m.id === id)?.name || "—";

    return (
        <Fragment>
            <Breadcrumb title="Loyalties" parent="Loyalty" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <Tile label="Total Programs" value={tiles.total} sub={`${tiles.active} active`} icon="🎯" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                        <Tile label="Stamps Programs" value={tiles.stamps} sub="Stamp-based loyalty" icon="☕" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={80} />
                        <Tile label="Percentage Programs" value={tiles.percentage} sub="Points-based loyalty" icon="💎" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={160} />
                        <Tile label="Active Now" value={tiles.active} sub={tiles.total > 0 ? `${Math.round((tiles.active / tiles.total) * 100)}% of programs` : "No programs yet"} icon="✓" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={240} />
                    </div>

                    <RCard
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <span>🎯 Loyalty Programs <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>· {tiles.total} total</span></span>
                                <Link to="/merchant/add-loyalty" style={buttonStyle}>+ Create Loyalty</Link>
                            </div>
                        }
                    >
                        {isLoading ? (
                            <Loader />
                        ) : loyaltyData.length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                No loyalty programs yet. Click <strong>+ Create Loyalty</strong> to add one.
                            </div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>Name</th>
                                            <th style={tbl.th}>Loyalty Type</th>
                                            {userType !== "merchant" && <th style={tbl.th}>Merchant</th>}
                                            <th style={tbl.th}>Status</th>
                                            <th style={tbl.th}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loyaltyData.map((loyalty) => (
                                            <tr key={loyalty.id} className="rpt-row">
                                                <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>{loyalty?.name || "—"}</td>
                                                <td style={tbl.td}>
                                                    {loyalty?.loyaltyType === "stamps" ? (
                                                        <Badge color={C.orange} bg={C.orangeLight}>STAMPS</Badge>
                                                    ) : (
                                                        <Badge color={C.blue} bg={C.blueLight}>PERCENTAGE</Badge>
                                                    )}
                                                </td>
                                                {userType !== "merchant" && (
                                                    <td style={{ ...tbl.td, color: C.textMed }}>{merchantNameById(loyalty.merchantId)}</td>
                                                )}
                                                <td style={tbl.td}>
                                                    {loyalty?.isActive ? (
                                                        <Badge color={C.green} bg={C.greenLight}>● Active</Badge>
                                                    ) : (
                                                        <Badge color={C.coral} bg={C.coralLight}>● Inactive</Badge>
                                                    )}
                                                </td>
                                                <td style={tbl.td}>
                                                    <button
                                                        onClick={() => navigate(`/merchant/edit-loyalty/${loyalty.id}?merchantId=${loyalty.merchantId}`)}
                                                        style={{
                                                            padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                                                            border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
                                                            display: "inline-flex", alignItems: "center", gap: 6,
                                                        }}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </RCard>
                </div>
            </Container>
        </Fragment>
    );
};

export default Loyalty;
